import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Badge,
  Spinner,
  Alert,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Input,
  Label,
  FormGroup
} from 'reactstrap';
import { User, Mail, Phone, MapPin, Building, Calendar, CreditCard, Package, Receipt, FileText, ShoppingBag, Gift, ChevronDown } from 'lucide-react';
import { getCustomers } from './Api/GmsCustomer';
import { getCustomerMemberships } from './Api/GmsCustomerMembership';
import { getBookings } from './Api/GmsBooking';
import { getSalesInvoices, getCustomerAdvanceBalance, getCustomerOutstandingCredit } from './Api/GmsBilling';
import { getBranches } from './Api/GmsBranch';
import { getEmployees } from './Api/GmsEmployee';
import { getCountries } from './Api/GmsCountry';
import { getSpaServices } from './Api/GmsSpaService';
import { getSpaPackages } from './Api/GmsSpaPackage';
import { getSpaPackageSeries } from './Api/GmsSpaPackageSeries';
import { getBusinesses } from './Api/GmsBusiness';
import { getProducts } from './Api/GmsProduct';
import Select from 'react-select';
import classnames from 'classnames';

const CustomerProfileModal = ({ 
  isOpen, 
  toggle, 
  customerId = null,
  customer = null
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [advanceBalance, setAdvanceBalance] = useState(null);
  const [outstandingCredit, setOutstandingCredit] = useState(null);
  const [branches, setBranches] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [packageSeries, setPackageSeries] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [products, setProducts] = useState([]);
  const [countries, setCountries] = useState([]);
  const [error, setError] = useState(null);
  const [timePeriod, setTimePeriod] = useState('1'); // 1 = Last 1 Month
  const [expandedMemberships, setExpandedMemberships] = useState(new Set()); // Track expanded membership rows

  // Notification preferences (UI only for now)
  const [notificationPrefs, setNotificationPrefs] = useState({
    transactional: { sms: true, email: true, whatsapp: false },
    marketing: { sms: true, email: true }
  });

  useEffect(() => {
    if (isOpen && (customerId || customer)) {
      loadCustomerData();
    }
  }, [isOpen, customerId, customer]);

  const loadCustomerData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (customer) {
        setCustomerData(customer);
      } else if (customerId) {
        const customers = await getCustomers({ customer_Id: customerId });
        if (customers && customers.length > 0) {
          setCustomerData(customers[0]);
        } else {
          setError('Customer not found');
          return;
        }
      }

      // Load all reference data
      const [branchesData, employeesData, countriesData, servicesData, packagesData, packageSeriesData, businessesData, productsData] = await Promise.all([
        getBranches().catch(() => []),
        getEmployees().catch(() => []),
        getCountries().catch(() => []),
        getSpaServices().catch(() => []),
        getSpaPackages().catch(() => []),
        getSpaPackageSeries().catch(() => []),
        getBusinesses().catch(() => []),
        getProducts({ status: true }).catch(() => [])
      ]);

      setBranches(branchesData);
      setEmployees(employeesData);
      setCountries(countriesData);
      setServices(servicesData);
      setPackages(packagesData);
      setPackageSeries(packageSeriesData);
      setBusinesses(businessesData);
      setProducts(productsData);

      // Load related data
      const currentCustomerId = customer?.customer_Id || customerId;
      if (currentCustomerId) {
        // Load invoices first so we can use businessId from them if needed
        await loadInvoices(currentCustomerId);
        
        // Then load other data in parallel
        await Promise.all([
          loadMemberships(currentCustomerId),
          loadBookings(currentCustomerId),
          loadFinancialData(currentCustomerId)
        ]);
      }
    } catch (err) {
      console.error('Error loading customer data:', err);
      setError('Failed to load customer data');
    } finally {
      setLoading(false);
    }
  };

  const loadMemberships = async (customerId) => {
    try {
      console.log('🔍 Loading memberships for customer:', customerId);
      const data = await getCustomerMemberships({ customerId });
      console.log('✅ Memberships loaded:', data);
      setMemberships(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('❌ Error loading memberships:', err);
      setMemberships([]);
    }
  };

  const loadBookings = async (customerId) => {
    try {
      console.log('🔍 Loading bookings for customer:', customerId);
      const response = await getBookings({ customer_Id: customerId, page: 1, pageSize: 10000 });
      console.log('✅ Bookings loaded:', response);
      
      // Handle paginated response
      let data = [];
      if (response && response.bookings) {
        data = response.bookings;
      } else if (Array.isArray(response)) {
        data = response;
      }
      
      const sortedBookings = Array.isArray(data) 
        ? data.sort((a, b) => new Date(b.booking_Date || b.entry_Date) - new Date(a.booking_Date || a.entry_Date))
        : [];
      console.log('📋 Sorted bookings:', sortedBookings.length);
      setAllBookings(sortedBookings);
    } catch (err) {
      console.error('❌ Error loading bookings:', err);
      setAllBookings([]);
    }
  };

  const loadInvoices = async (customerId) => {
    try {
      console.log('🔍 Loading invoices for customer:', customerId);
      const data = await getSalesInvoices({ Customer_Id: customerId });
      console.log('✅ Invoices loaded:', data);
      const sortedInvoices = Array.isArray(data)
        ? data.sort((a, b) => new Date(b.Invoice_Date) - new Date(a.Invoice_Date))
        : [];
      console.log('📋 Sorted invoices:', sortedInvoices.length);
      setInvoices(sortedInvoices);
    } catch (err) {
      console.error('❌ Error loading invoices:', err);
      setInvoices([]);
    }
  };

  const loadFinancialData = async (customerId) => {
    try {
      console.log('🔍 Loading financial data for customer:', customerId);
      
      // Only send customerId - no businessId or branchId
      const [advance, credit] = await Promise.all([
        getCustomerAdvanceBalance(customerId).catch((err) => {
          console.error('❌ Error getting advance balance:', err);
          return null;
        }),
        getCustomerOutstandingCredit(customerId).catch((err) => {
          console.error('❌ Error getting outstanding credit:', err);
          return null;
        })
      ]);
      
      console.log('✅ Financial data loaded:', { advance, credit });
      
      setAdvanceBalance(advance);
      setOutstandingCredit(credit);
    } catch (err) {
      console.error('❌ Error loading financial data:', err);
    }
  };

  // Calculate metrics
  const calculateMetrics = () => {
    const now = new Date();
    const periodStart = new Date();
    
    // Calculate period start based on timePeriod
    if (timePeriod === '1') {
      periodStart.setMonth(periodStart.getMonth() - 1);
    } else if (timePeriod === '3') {
      periodStart.setMonth(periodStart.getMonth() - 3);
    } else if (timePeriod === '6') {
      periodStart.setMonth(periodStart.getMonth() - 6);
    } else if (timePeriod === '12') {
      periodStart.setMonth(periodStart.getMonth() - 12);
    }

    // Filter bookings within period
    const periodBookings = allBookings.filter(booking => {
      const bookingDate = new Date(booking.booking_Date || booking.entry_Date);
      return bookingDate >= periodStart;
    });

    // Total visits (completed bookings)
    const totalVisits = periodBookings.filter(b => !b.is_Cancelled).length;

    // Open appointments (future bookings not cancelled)
    const openAppointments = allBookings.filter(booking => {
      // Use booking_Date with from_Time, or from_Time, or entry_Date as fallback
      let appointmentDateTime = null;
      if (booking.from_Time) {
        appointmentDateTime = new Date(booking.from_Time);
      } else if (booking.booking_Date) {
        appointmentDateTime = new Date(booking.booking_Date);
      } else {
        appointmentDateTime = new Date(booking.entry_Date);
      }
      return appointmentDateTime >= now && !booking.is_Cancelled;
    }).length;

    // Amount due (outstanding credit)
    const amountDue = outstandingCredit?.totalOutstanding || outstandingCredit?.TotalOutstanding || 0;

    // Membership balance (calculate from memberships)
    let membershipBalance = 0;
    memberships.forEach(membership => {
      if (membership.installments && Array.isArray(membership.installments)) {
        membership.installments.forEach(installment => {
          const available = (installment.amount || 0) - (installment.paidAmount || installment.billedAmount || 0);
          if (available > 0) {
            membershipBalance += available;
          }
        });
      }
    });

    return {
      totalVisits,
      openAppointments,
      amountDue,
      membershipBalance
    };
  };

  const metrics = calculateMetrics();

  // Get upcoming appointments
  const getUpcomingAppointments = () => {
    const now = new Date();
    return allBookings
      .filter(booking => {
        // Use booking_Date with from_Time, or from_Time, or entry_Date as fallback
        let appointmentDateTime = null;
        if (booking.from_Time) {
          appointmentDateTime = new Date(booking.from_Time);
        } else if (booking.booking_Date && booking.from_Time) {
          // Combine booking_Date with from_Time if from_Time is just time
          const bookingDate = new Date(booking.booking_Date);
          const fromTime = new Date(booking.from_Time);
          appointmentDateTime = new Date(bookingDate);
          appointmentDateTime.setHours(fromTime.getHours(), fromTime.getMinutes(), fromTime.getSeconds());
        } else if (booking.booking_Date) {
          appointmentDateTime = new Date(booking.booking_Date);
        } else {
          appointmentDateTime = new Date(booking.entry_Date);
        }
        
        return appointmentDateTime >= now && !booking.is_Cancelled;
      })
      .sort((a, b) => {
        const aTime = a.from_Time ? new Date(a.from_Time) : (a.booking_Date ? new Date(a.booking_Date) : new Date(a.entry_Date));
        const bTime = b.from_Time ? new Date(b.from_Time) : (b.booking_Date ? new Date(b.booking_Date) : new Date(b.entry_Date));
        return aTime - bTime;
      })
      .slice(0, 5);
  };

  // Get past appointments
  const getPastAppointments = () => {
    const now = new Date();
    return allBookings
      .filter(booking => {
        // Use booking_Date with from_Time, or from_Time, or entry_Date as fallback
        let appointmentDateTime = null;
        if (booking.from_Time) {
          appointmentDateTime = new Date(booking.from_Time);
        } else if (booking.booking_Date && booking.from_Time) {
          const bookingDate = new Date(booking.booking_Date);
          const fromTime = new Date(booking.from_Time);
          appointmentDateTime = new Date(bookingDate);
          appointmentDateTime.setHours(fromTime.getHours(), fromTime.getMinutes(), fromTime.getSeconds());
        } else if (booking.booking_Date) {
          appointmentDateTime = new Date(booking.booking_Date);
        } else {
          appointmentDateTime = new Date(booking.entry_Date);
        }
        
        return appointmentDateTime < now || booking.is_Cancelled;
      })
      .sort((a, b) => {
        const aTime = a.from_Time ? new Date(a.from_Time) : (a.booking_Date ? new Date(a.booking_Date) : new Date(a.entry_Date));
        const bTime = b.from_Time ? new Date(b.from_Time) : (b.booking_Date ? new Date(b.booking_Date) : new Date(b.entry_Date));
        return bTime - aTime;
      })
      .slice(0, 10);
  };

  // Get employee name
  const getEmployeeName = (employeeId) => {
    if (!employeeId) return '-';
    
    const employee = employees.find(e => 
      e.employee_Id === employeeId || 
      e.employeeId === employeeId ||
      e.value === employeeId ||
      e.id === employeeId
    );
    
    if (employee) {
      return employee.employee_NameEn || employee.employeeName_En || employee.label || employee.name || '-';
    }
    
    return '-';
  };

  // Get service name from service ID
  const getProductNameById = (productId) => {
    if (!productId) return '-';
    const product = products.find(s => s.product_Id === productId);
    return product ? product.product_NameEn : '-';
  };

  const getServiceNameById = (serviceId) => {
    if (!serviceId) return '-';
    const service = services.find(s => 
      s.service_Id === serviceId || 
      s.spaService_Id === serviceId ||
      s.value === serviceId ||
      s.id === serviceId
    );
    if (service) {
      return service.spaServiceName_En || service.spaService_NameEn || service.service_NameEn || service.serviceName_En || service.label || service.name || '-';
    }
    return '-';
  };

  // Get service cost from service ID
  const getServiceCostById = (serviceId) => {
    if (!serviceId) return 0;
    const service = services.find(s => 
      s.service_Id === serviceId || 
      s.spaService_Id === serviceId ||
      s.value === serviceId ||
      s.id === serviceId
    );
    if (service) {
      return parseFloat(service.cost || service.Cost || 0);
    }
    return 0;
  };

  // Expand bookings into individual service rows
  const expandBookingsToServices = (bookings) => {
    const serviceRows = [];
    bookings.forEach((booking, bookingIndex) => {
      // Get appointment date from booking
      const appointmentDate = booking.from_Time 
        ? new Date(booking.from_Time).toISOString().split('T')[0]
        : (booking.booking_Date 
          ? new Date(booking.booking_Date).toISOString().split('T')[0]
          : booking.entry_Date);

      // Check if booking has details with resourceDetails
      if (booking.details && Array.isArray(booking.details) && booking.details.length > 0) {
        booking.details.forEach((detail, detailIndex) => {
          if (detail.resourceDetails && Array.isArray(detail.resourceDetails) && detail.resourceDetails.length > 0) {
            // Create a row for each service in resourceDetails
            detail.resourceDetails.forEach((resource, resourceIndex) => {
              const serviceName = resource.service_Id ? getServiceNameById(resource.service_Id) : '-';
              const serviceCost = resource.service_Id ? getServiceCostById(resource.service_Id) : 0;
              const serviceTime = resource.from_Time && resource.to_Time
                ? `${new Date(resource.from_Time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} - ${new Date(resource.to_Time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
                : '-';
              const employeeName = resource.employee_Id ? getEmployeeName(resource.employee_Id) : '-';
              
              // Calculate duration
              let duration = null;
              if (resource.from_Time && resource.to_Time) {
                duration = Math.round((new Date(resource.to_Time) - new Date(resource.from_Time)) / (1000 * 60));
              }
              
              serviceRows.push({
                booking_Id: booking.booking_Id,
                bookingIndex: bookingIndex,
                detailIndex: detailIndex,
                resourceIndex: resourceIndex,
                date: appointmentDate,
                serviceName: serviceName + (duration ? ` (${duration}mins)` : ''),
                serviceCost: serviceCost,
                time: serviceTime,
                staff: employeeName,
                booking: booking,
                resource: resource
              });
            });
          } else {
            // If no resourceDetails, create a single row for the booking
            const serviceName = getServiceName(booking);
            // Try to get service cost from first resource detail if available
            let serviceCost = 0;
            if (detail.resourceDetails && detail.resourceDetails.length > 0 && detail.resourceDetails[0].service_Id) {
              serviceCost = getServiceCostById(detail.resourceDetails[0].service_Id);
            } else if (detail.entity_Type === 'ser' || detail.entity_Type === 'Service') {
              serviceCost = getServiceCostById(detail.entity_Id);
            }
            const serviceTime = booking.from_Time && booking.to_Time
              ? `${new Date(booking.from_Time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} - ${new Date(booking.to_Time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
              : '-';
            const employeeName = booking.details && booking.details[0]?.resourceDetails?.[0]?.employee_Id
              ? getEmployeeName(booking.details[0].resourceDetails[0].employee_Id)
              : '-';
            
            serviceRows.push({
              booking_Id: booking.booking_Id,
              bookingIndex: bookingIndex,
              detailIndex: detailIndex,
              resourceIndex: 0,
              date: appointmentDate,
              serviceName: serviceName,
              serviceCost: serviceCost,
              time: serviceTime,
              staff: employeeName,
              booking: booking,
              resource: null
            });
          }
        });
      } else {
        // If no details, create a single row for the booking
        const serviceName = getServiceName(booking);
        const serviceCost = 0; // No service ID available
        const serviceTime = booking.from_Time && booking.to_Time
          ? `${new Date(booking.from_Time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} - ${new Date(booking.to_Time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
          : '-';
        const employeeName = '-';
        
        serviceRows.push({
          booking_Id: booking.booking_Id,
          bookingIndex: bookingIndex,
          detailIndex: 0,
          resourceIndex: 0,
          date: appointmentDate,
          serviceName: serviceName,
          serviceCost: serviceCost,
          time: serviceTime,
          staff: employeeName,
          booking: booking,
          resource: null
        });
      }
    });
    return serviceRows;
  };

  // Get entity name from membership
  const getEntityName = (membership) => {
    console.log('🔍 Getting entity name for membership:', membership);
    
    const entityType = membership.entity_Type || membership.entityType;
    const entityId = membership.entity_Id || membership.entityId;
    
    console.log('📋 Entity Type:', entityType, 'Entity ID:', entityId);
    
    if (!entityType || !entityId) {
      console.log('⚠️ Missing entity type or ID');
      return '-';
    }
    
    // Check if entity_Type is numeric (1=Service, 2=Package, 3=PackageSeries) or string (SER, PAC, etc.)
    if (entityType === 1 || entityType === 'SER' || entityType === 'ser' || entityType === 'Service') {
      // Service - but memberships are usually packages or series, not services directly
      console.log('⚠️ Service type in membership - unusual');
      return 'Service';
    } else if (entityType === 2 || entityType === 'PAC' || entityType === 'pac' || entityType === 'Package') {
      console.log('🔍 Looking for package ID:', entityId);
      const pkg = packages.find(p => 
        p.package_Id === entityId || 
        p.packageId === entityId ||
        p.value === entityId ||
        p.id === entityId
      );
      console.log('✅ Found package:', pkg);
      if (pkg) {
        return pkg.packageName_En || pkg.package_NameEn || pkg.label || pkg.name || 'Package';
      }
    } else if (entityType === 3 || entityType === 'SER' || entityType === 'sep' || entityType === 'PackageSeries') {
      console.log('🔍 Looking for package series ID:', entityId);
      const series = packageSeries.find(s => 
        s.series_Id === entityId || 
        s.seriesId === entityId ||
        s.value === entityId ||
        s.id === entityId
      );
      console.log('✅ Found package series:', series);
      if (series) {
        return series.seriesName_En || series.series_NameEn || series.label || series.name || 'Package Series';
      }
    }
    
    console.log('⚠️ Could not find entity name');
    return '-';
  };

  // Get products from invoices
  const getProductsFromInvoices = () => {
    console.log('🔍 Getting products from invoices:', invoices.length);
    
    const productMap = new Map();
    
    invoices.forEach(invoice => {
      console.log('📋 Processing invoice:', invoice.Invoice_Id || invoice.invoice_Id, invoice);
      console.log('📦 Invoice structure:', {
        hasSelectedProducts: !!invoice.selectedProducts,
        hasDetails: !!invoice.details,
        detailsLength: invoice.details?.length,
        invoiceKeys: Object.keys(invoice)
      });
      
      // Check if invoice has selectedProducts array
      if (invoice.selectedProducts && Array.isArray(invoice.selectedProducts)) {
        console.log('📦 Found selectedProducts:', invoice.selectedProducts.length);
        invoice.selectedProducts.forEach(product => {
          const productId = product.product_Id || product.productId || product.id;
          const quantity = product.quantity || product.qty || 0;
          
          if (productId) {
            const existing = productMap.get(productId) || { product, totalQuantity: 0, totalAmount: 0 };
            existing.totalQuantity += quantity;
            existing.totalAmount += (product.price || product.amount || 0) * quantity;
            productMap.set(productId, existing);
            console.log('✅ Added product from selectedProducts:', productId, quantity);
          }
        });
      }
      
      // Check invoice details (if structured differently)
      if (invoice.details && Array.isArray(invoice.details)) {
        console.log('📦 Processing details array:', invoice.details.length);
        invoice.details.forEach((detail, index) => {
          console.log(`📦 Detail ${index}:`, detail);
          
          // Check for product in detail
          if (detail.product_Id || detail.productId) {
            const productId = detail.product_Id || detail.productId;
            const quantity = detail.quantity || detail.qty || 1;
            const price = detail.price || detail.amount || detail.netAmount || 0;
            
            const existing = productMap.get(productId) || { 
              product: detail, 
              totalQuantity: 0, 
              totalAmount: 0 
            };
            existing.totalQuantity += quantity;
            existing.totalAmount += price * quantity;
            productMap.set(productId, existing);
            console.log('✅ Added product from details:', productId, quantity, price);
          }
          
          // Also check if detail has entityType or entity_Type === 'pro' or 'product'
          const entityType = detail.entityType || detail.entity_Type;
          if (entityType === 'pro' || entityType === 'product' || entityType === 'Product') {
            const productId = detail.entityId || detail.entity_Id;
            if (productId) {
              const quantity = detail.quantity || detail.qty || 1;
              const price = detail.price || detail.amount || detail.netAmount || 0;
              
              // Convert productId to number for consistent comparison
              const productIdNum = parseInt(productId, 10);
              const existing = productMap.get(productIdNum) || { 
                product: detail, 
                productId: productIdNum,
                totalQuantity: 0, 
                totalAmount: 0 
              };
              existing.totalQuantity += quantity;
              existing.totalAmount += price * quantity;
              productMap.set(productIdNum, existing);
              console.log('✅ Added product from entity:', productIdNum, quantity, price);
            }
          }
        });
      }
    });
    
    console.log('✅ Products extracted:', Array.from(productMap.values()));
    return Array.from(productMap.values());
  };

  // Get service name from booking
  const getServiceName = (booking) => {
    console.log('🔍 Getting service name for booking:', booking.booking_Id, booking);
    
    if (!booking.details || !Array.isArray(booking.details) || booking.details.length === 0) {
      console.log('⚠️ No details found in booking');
      return '-';
    }
    
    // Get first detail
    const firstDetail = booking.details[0];
    console.log('📋 First detail:', firstDetail);
    
    // Check entity_Type first - this tells us if it's a package, service, or package series
    if (firstDetail.entity_Type) {
      console.log('🔍 Checking entity type:', firstDetail.entity_Type, 'entity ID:', firstDetail.entity_Id);
      
      // If it's a package
      if (firstDetail.entity_Type === 'pac' || firstDetail.entity_Type === 'Package') {
        const packageId = firstDetail.entity_Id;
        console.log('🔍 Looking for package ID from entity:', packageId);
        const pkg = packages.find(p => 
          p.package_Id === packageId || 
          p.packageId === packageId ||
          p.value === packageId ||
          p.id === packageId
        );
        console.log('✅ Found package:', pkg);
        if (pkg) {
          const packageName = pkg.packageName_En || pkg.package_NameEn || pkg.label || pkg.name || 'Package';
          
          // Get all services within this package
          const serviceNames = [];
          let totalDuration = 0;
          
          if (firstDetail.resourceDetails && Array.isArray(firstDetail.resourceDetails)) {
            firstDetail.resourceDetails.forEach(resource => {
              if (resource.service_Id) {
                const service = services.find(s => 
                  s.service_Id === resource.service_Id || 
                  s.spaService_Id === resource.service_Id ||
                  s.value === resource.service_Id ||
                  s.id === resource.service_Id
                );
                
                if (service) {
                  const serviceName = service.spaServiceName_En || service.spaService_NameEn || service.service_NameEn || service.serviceName_En || service.label || service.name || 'Service';
                  let serviceDuration = 0;
                  
                  if (resource.from_Time && resource.to_Time) {
                    serviceDuration = Math.round((new Date(resource.to_Time) - new Date(resource.from_Time)) / (1000 * 60));
                    totalDuration += serviceDuration;
                  }
                  
                  serviceNames.push(serviceName + (serviceDuration > 0 ? ` (${serviceDuration}mins)` : ''));
                }
              }
            });
          }
          
          console.log('✅ Package name resolved:', packageName, 'Services:', serviceNames, 'Total duration:', totalDuration);
          
          // Format: "Package Name - Service 1 (55mins), Service 2 (90mins) (145mins total)"
          if (serviceNames.length > 0) {
            return packageName + ' - ' + serviceNames.join(', ') + (totalDuration > 0 ? ` (${totalDuration}mins total)` : '');
          } else {
            return packageName + (totalDuration > 0 ? ` (${totalDuration}mins)` : '');
          }
        }
      }
      
      // If it's a package series (sep = series)
      if (firstDetail.entity_Type === 'sep' || firstDetail.entity_Type === 'PackageSeries') {
        const seriesId = firstDetail.entity_Id;
        console.log('🔍 Looking for package series ID from entity:', seriesId);
        const series = packageSeries.find(s => 
          s.series_Id === seriesId || 
          s.seriesId === seriesId ||
          s.value === seriesId ||
          s.id === seriesId
        );
        console.log('✅ Found package series:', series);
        if (series) {
          const seriesName = series.seriesName_En || series.series_NameEn || series.label || series.name || 'Package Series';
          console.log('✅ Package series name resolved:', seriesName);
          return seriesName;
        }
      }
      
      // If it's a service (ser = service)
      if (firstDetail.entity_Type === 'ser' || firstDetail.entity_Type === 'Service') {
        const serviceId = firstDetail.entity_Id;
        console.log('🔍 Looking for service ID from entity:', serviceId);
        const service = services.find(s => 
          s.service_Id === serviceId || 
          s.spaService_Id === serviceId ||
          s.value === serviceId ||
          s.id === serviceId
        );
        if (service) {
          const serviceName = service.spaServiceName_En || service.spaService_NameEn || service.service_NameEn || service.serviceName_En || service.label || 'Service';
          console.log('✅ Service name resolved:', serviceName);
          return serviceName;
        }
      }
    }
    
    // Fallback: Check resource details
    if (firstDetail.resourceDetails && Array.isArray(firstDetail.resourceDetails) && firstDetail.resourceDetails.length > 0) {
      const firstResource = firstDetail.resourceDetails[0];
      console.log('🔧 First resource:', firstResource);
      console.log('📦 Available services:', services.length);
      console.log('📦 Available packages:', packages.length);
      
      // Check for package in resource
      if (firstResource.package_Id) {
        console.log('🔍 Looking for package ID from resource:', firstResource.package_Id);
        const pkg = packages.find(p => 
          p.package_Id === firstResource.package_Id || 
          p.packageId === firstResource.package_Id ||
          p.value === firstResource.package_Id ||
          p.id === firstResource.package_Id
        );
        console.log('✅ Found package:', pkg);
        
        if (pkg) {
          const packageName = pkg.packageName_En || pkg.package_NameEn || pkg.label || pkg.name || 'Package';
          console.log('✅ Package name resolved:', packageName);
          return packageName;
        }
      }
      
      // Check for service in resource
      if (firstResource.service_Id) {
        console.log('🔍 Looking for service ID from resource:', firstResource.service_Id);
        const service = services.find(s => 
          s.service_Id === firstResource.service_Id || 
          s.spaService_Id === firstResource.service_Id ||
          s.value === firstResource.service_Id ||
          s.id === firstResource.service_Id
        );
        console.log('✅ Found service:', service);
        
        if (service) {
          // Calculate total duration from all resources in this detail
          let totalDuration = 0;
          if (firstDetail.resourceDetails.length > 1) {
            // Multiple services - calculate total duration
            firstDetail.resourceDetails.forEach(resource => {
              if (resource.from_Time && resource.to_Time) {
                const duration = Math.round((new Date(resource.to_Time) - new Date(resource.from_Time)) / (1000 * 60));
                totalDuration += duration;
              }
            });
          } else {
            // Single service - use its duration
            if (firstResource.from_Time && firstResource.to_Time) {
              totalDuration = Math.round((new Date(firstResource.to_Time) - new Date(firstResource.from_Time)) / (1000 * 60));
            }
          }
          
          const serviceName = service.spaServiceName_En || service.spaService_NameEn || service.service_NameEn || service.serviceName_En || service.label || service.name || 'Service';
          console.log('✅ Service name resolved:', serviceName, 'Duration:', totalDuration);
          return serviceName + (totalDuration > 0 ? ` (${totalDuration}mins)` : '');
        }
      }
      
    }
    
    console.log('⚠️ Could not find service name');
    return '-';
  };

  // Get booking amount (from invoice if linked, otherwise from service amounts)
  const getBookingAmount = (booking) => {
    console.log('🔍 Getting booking amount for booking:', booking.booking_Id);
    
    // First, try to get from invoice if linked
    if (booking.invoice_Id) {
      const invoice = invoices.find(inv => inv.Invoice_Id === booking.invoice_Id);
      if (invoice) {
        console.log('✅ Found invoice amount:', invoice.Invoice_NetAmount);
        return parseFloat(invoice.Invoice_NetAmount || 0);
      }
    }
    
    // If no invoice, calculate from service amounts
    let total = 0;
    if (booking.details && Array.isArray(booking.details)) {
      booking.details.forEach(detail => {
        // Check if it's a package - use entity_Id to find package
        if (detail.entity_Type === 'pac' || detail.entity_Type === 'Package') {
          const packageId = detail.entity_Id;
          const pkg = packages.find(p => 
            p.package_Id === packageId || 
            p.packageId === packageId ||
            p.value === packageId ||
            p.id === packageId
          );
          if (pkg) {
            const packageAmount = parseFloat(pkg.packageAmount || pkg.amount || pkg.netAmount || 0);
            console.log('📦 Found package amount:', packageAmount);
            total += packageAmount;
          }
        }
        // Check if it's a service - use entity_Id to find service
        else if (detail.entity_Type === 'ser' || detail.entity_Type === 'Service') {
          const serviceId = detail.entity_Id;
          const service = services.find(s => 
            s.service_Id === serviceId || 
            s.spaService_Id === serviceId ||
            s.value === serviceId ||
            s.id === serviceId
          );
          if (service) {
            const serviceAmount = parseFloat(service.amount || service.netAmount || service.spaServiceAmount || 0);
            console.log('🔧 Found service amount:', serviceAmount);
            total += serviceAmount;
          }
        }
        // If no entity type, try to get from resourceDetails
        else if (detail.resourceDetails && Array.isArray(detail.resourceDetails)) {
          detail.resourceDetails.forEach(resource => {
            // First try resource.cost
            if (resource.cost && parseFloat(resource.cost) > 0) {
              total += parseFloat(resource.cost);
            }
            // Otherwise, look up service by service_Id
            else if (resource.service_Id) {
              const service = services.find(s => 
                s.service_Id === resource.service_Id || 
                s.spaService_Id === resource.service_Id ||
                s.value === resource.service_Id ||
                s.id === resource.service_Id
              );
              if (service) {
                const serviceAmount = parseFloat(service.amount || service.netAmount || service.spaServiceAmount || 0);
                console.log('🔧 Found service amount from resource:', serviceAmount);
                total += serviceAmount;
              }
            }
            // Check for package
            else if (resource.package_Id) {
              const pkg = packages.find(p => 
                p.package_Id === resource.package_Id || 
                p.packageId === resource.package_Id ||
                p.value === resource.package_Id ||
                p.id === resource.package_Id
              );
              if (pkg) {
                const packageAmount = parseFloat(pkg.packageAmount || pkg.amount || pkg.netAmount || 0);
                console.log('📦 Found package amount from resource:', packageAmount);
                total += packageAmount;
              }
            }
          });
        }
      });
    }
    
    console.log('💰 Total booking amount:', total);
    return total;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).replace(/\//g, '/');
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '0.00';
    return parseFloat(amount).toFixed(2);
  };

  const getBusinessName = () => {
    const businessId = localStorage.getItem('businessId');
    if (businessId && businesses.length > 0) {
      const business = businesses.find(b => b.business_Id === parseInt(businessId));
      return business ? business.business_NameEn : '-';
    }
    return '-';
  };

  const getInitials = () => {
    if (!customerData?.customer_NameEn) return 'C';
    const names = customerData.customer_NameEn.split(' ');
    if (names.length >= 2) {
      return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
    }
    return customerData.customer_NameEn.charAt(0).toUpperCase();
  };

  // Toggle membership row expansion
  const toggleMembershipExpansion = (membershipId) => {
    const newExpanded = new Set(expandedMemberships);
    if (newExpanded.has(membershipId)) {
      newExpanded.delete(membershipId);
    } else {
      newExpanded.add(membershipId);
    }
    setExpandedMemberships(newExpanded);
  };

  // Process membership details to get services and products with quantities
  const getMembershipDetails = (membership) => {
    if (!membership.details || !Array.isArray(membership.details) || membership.details.length === 0) {
      return { services: [], products: [] };
    }

    // Group by package, then by service/product
    const groupedByPackage = membership.details.reduce((acc, detail) => {
      const packageKey = detail.packageId ? `pkg_${detail.packageId}` : 'direct';
      const packageId = detail.packageId || null;
      let packageName = 'Direct Items';
      if (packageId) {
        const pkg = packages.find(p => 
          p.package_Id === packageId || 
          p.packageId === packageId ||
          p.value === packageId ||
          p.id === packageId
        );
        packageName = pkg ? (pkg.packageName_En || pkg.package_NameEn || `Package ${packageId}`) : `Package ${packageId}`;
      }
      
      if (!acc[packageKey]) {
        acc[packageKey] = {
          packageId: packageId,
          packageName: packageName,
          services: {},
          products: {}
        };
      }
      
      // Process services
      if (detail.serviceId) {
        const serviceKey = `svc_${detail.serviceId}`;
        if (!acc[packageKey].services[serviceKey]) {
          acc[packageKey].services[serviceKey] = {
            serviceId: detail.serviceId,
            serviceName: getServiceNameById(detail.serviceId),
            totalQty: 0,
            availableQty: 0,
            completedQty: 0,
            inProgressQty: 0,
            pendingQty: 0
          };
        }
        acc[packageKey].services[serviceKey].totalQty++;
        const status = (detail.completionStatus || 'Pending').toString().trim().toLowerCase();
        if (status === 'completed' || status === 'complete') {
          acc[packageKey].services[serviceKey].completedQty++;
        } else if (status === 'in progress' || status === 'inprogress' || status === 'check in' || status === 'checkin') {
          acc[packageKey].services[serviceKey].inProgressQty++;
        } else {
          acc[packageKey].services[serviceKey].pendingQty++;
          acc[packageKey].services[serviceKey].availableQty++;
        }
      }
      
      // Process products
      if (detail.productId) {
        const productKey = `prod_${detail.productId}`;
        if (!acc[packageKey].products[productKey]) {
          acc[packageKey].products[productKey] = {
            productId: detail.productId,
            productName: getProductNameById(detail.productId),
            totalQty: 0,
            availableQty: 0,
            completedQty: 0,
            inProgressQty: 0,
            pendingQty: 0
          };
        }
        acc[packageKey].products[productKey].totalQty++;
        const status = (detail.completionStatus || 'Pending').toString().trim().toLowerCase();
        if (status === 'completed' || status === 'complete') {
          acc[packageKey].products[productKey].completedQty++;
        } else if (status === 'in progress' || status === 'inprogress' || status === 'check in' || status === 'checkin') {
          acc[packageKey].products[productKey].inProgressQty++;
        } else {
          acc[packageKey].products[productKey].pendingQty++;
          acc[packageKey].products[productKey].availableQty++;
        }
      }
      
      return acc;
    }, {});

    // Convert to arrays
    const result = { services: [], products: [] };
    
    Object.values(groupedByPackage).forEach(packageData => {
      // Add services
      Object.values(packageData.services).forEach(service => {
        result.services.push({
          ...service,
          packageId: packageData.packageId,
          packageName: packageData.packageName
        });
      });
      
      // Add products
      Object.values(packageData.products).forEach(product => {
        result.products.push({
          ...product,
          packageId: packageData.packageId,
          packageName: packageData.packageName
        });
      });
    });

    return result;
  };

  if (!isOpen) return null;

  const upcomingAppointments = getUpcomingAppointments();
  const pastAppointments = getPastAppointments();

  return (
    <Modal 
      isOpen={isOpen} 
      toggle={toggle} 
      size="xl" 
      className="customer-profile-modal"
      backdrop="static"
      style={{ maxWidth: '95%' }}
    >
      <ModalHeader toggle={toggle} className="bg-light border-bottom">
        <div className="d-flex align-items-center justify-content-between w-100">
          <h5 className="mb-0">
            {customerData?.customer_NameEn || 'Customer Profile'}
          </h5>
        </div>
      </ModalHeader>
      <ModalBody style={{ maxHeight: '85vh', overflowY: 'auto', padding: 0 }}>
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2">Loading customer data...</p>
          </div>
        ) : error ? (
          <Alert color="danger" className="m-3">{error}</Alert>
        ) : !customerData ? (
          <Alert color="warning" className="m-3">No customer data available</Alert>
        ) : (
          <div>
            {/* Top Navigation Tabs */}
            <Nav tabs className="border-bottom px-3" style={{ backgroundColor: '#f8f9fa' }}>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'overview' })}
                  onClick={() => setActiveTab('overview')}
                  style={{ cursor: 'pointer' }}
                >
                  Overview
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'profile' })}
                  onClick={() => setActiveTab('profile')}
                  style={{ cursor: 'pointer' }}
                >
                  Profile
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'notes' })}
                  onClick={() => setActiveTab('notes')}
                  style={{ cursor: 'pointer' }}
                >
                  Notes
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'appointment' })}
                  onClick={() => setActiveTab('appointment')}
                  style={{ cursor: 'pointer' }}
                >
                  Appointment
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'products' })}
                  onClick={() => setActiveTab('products')}
                  style={{ cursor: 'pointer' }}
                >
                  Products
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'memberships' })}
                  onClick={() => setActiveTab('memberships')}
                  style={{ cursor: 'pointer' }}
                >
                  Memberships ({memberships.length})
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'packages' })}
                  onClick={() => setActiveTab('packages')}
                  style={{ cursor: 'pointer' }}
                >
                  Packages
                </NavLink>
              </NavItem>
            </Nav>

            <TabContent activeTab={activeTab} className="p-3">
              {/* Overview Tab */}
              <TabPane tabId="overview">
                <Row>
                  {/* Left Sidebar - Customer Summary */}
                  <Col md={4} className="border-end pe-3">
                    <Card className="border-0 shadow-sm mb-3">
                      <CardBody className="text-center">
                        <div className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" 
                             style={{ width: '80px', height: '80px', fontSize: '32px', fontWeight: 'bold' }}>
                          {getInitials()}
                        </div>
                        <h5 className="mb-2">{customerData.customer_NameEn}</h5>
                        <p className="text-muted mb-3">
                          <Phone size={14} className="me-1" />
                          {customerData.customer_ContactNo || '-'}
                        </p>
                        
                        <div className="mb-3">
                          <Select
                            value={{ value: timePeriod, label: timePeriod === '1' ? 'Last 1 Month' : timePeriod === '3' ? 'Last 3 Months' : timePeriod === '6' ? 'Last 6 Months' : 'Last 12 Months' }}
                            onChange={(option) => setTimePeriod(option.value)}
                            options={[
                              { value: '1', label: 'Last 1 Month' },
                              { value: '3', label: 'Last 3 Months' },
                              { value: '6', label: 'Last 6 Months' },
                              { value: '12', label: 'Last 12 Months' }
                            ]}
                            classNamePrefix="select2-selection"
                            isSearchable={false}
                          />
                        </div>

                        <div className="text-start">
                          <div className="mb-2">
                            <strong>Total visits:</strong> {metrics.totalVisits}
                          </div>
                          <div className="mb-2">
                            <strong>Total open Appointments:</strong> {metrics.openAppointments}
                          </div>
                          <div className="mb-2">
                            <strong>Amount due:</strong> {formatCurrency(metrics.amountDue)} AED
                          </div>
                          <div className="mb-2">
                            <strong>Current membership balance:</strong> {formatCurrency(metrics.membershipBalance)} AED
                          </div>
                        </div>

                        <div className="mt-3 pt-3 border-top">
                          <small className="text-muted">{getBusinessName()}</small>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>

                  {/* Main Content Area */}
                  <Col md={8}>
                    {/* Upcoming Appointments */}
                    <Card className="mb-3 border-0 shadow-sm">
                      <CardBody>
                        <h6 className="mb-3">Upcoming Appointments</h6>
                        {upcomingAppointments.length === 0 ? (
                          <Alert color="info" className="mb-0">
                            There are no upcoming Appointments for this Guest
                          </Alert>
                        ) : (
                          <Table responsive size="sm">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Service</th>
                                <th>Time</th>
                                <th>Staff</th>
                              </tr>
                            </thead>
                            <tbody>
                              {expandBookingsToServices(upcomingAppointments).map((serviceRow, index) => (
                                <tr key={`${serviceRow.booking_Id}-${serviceRow.detailIndex}-${serviceRow.resourceIndex}-${index}`}>
                                  <td>{formatDate(serviceRow.date)}</td>
                                  <td>{serviceRow.serviceName}</td>
                                  <td>{serviceRow.time}</td>
                                  <td>{serviceRow.staff}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </CardBody>
                    </Card>

                    {/* Past Appointments */}
                    <Card className="mb-3 border-0 shadow-sm">
                      <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="mb-0">Past Appointments ({pastAppointments.length})</h6>
                          {pastAppointments.length > 3 && (
                            <Button color="link" size="sm">More</Button>
                          )}
                        </div>
                        {pastAppointments.length === 0 ? (
                          <Alert color="info" className="mb-0">No past appointments</Alert>
                        ) : (
                          <Table responsive size="sm">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Service</th>
                                <th>Cost</th>
                                <th>Staff</th>
                              </tr>
                            </thead>
                            <tbody>
                              {expandBookingsToServices(pastAppointments.slice(0, 3)).map((serviceRow, index) => (
                                <tr key={`${serviceRow.booking_Id}-${serviceRow.detailIndex}-${serviceRow.resourceIndex}-${index}`}>
                                  <td>{formatDate(serviceRow.date)}</td>
                                  <td>{serviceRow.serviceName}</td>
                                  <td>{formatCurrency(serviceRow.serviceCost || 0)} AED</td>
                                  <td>{serviceRow.staff}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </CardBody>
                    </Card>

                    {/* Packages */}
                    <Card className="mb-3 border-0 shadow-sm">
                      <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="mb-0">Packages</h6>
                          {memberships.filter(m => {
                            const entityType = m.entity_Type || m.entityType;
                            return entityType === 2 || entityType === 3 || 
                                   entityType === 'pac' || entityType === 'PAC' || entityType === 'Package' ||
                                   entityType === 'sep' || entityType === 'SER' || entityType === 'PackageSeries';
                          }).length > 1 && (
                            <Button color="link" size="sm">More</Button>
                          )}
                        </div>
                        {memberships.filter(m => {
                          const entityType = m.entity_Type || m.entityType;
                          return entityType === 2 || entityType === 3 || 
                                 entityType === 'pac' || entityType === 'PAC' || entityType === 'Package' ||
                                 entityType === 'sep' || entityType === 'SER' || entityType === 'PackageSeries';
                        }).length === 0 ? (
                          <Alert color="info" className="mb-0">No packages found</Alert>
                        ) : (
                          <div>
                            {memberships.filter(m => {
                              const entityType = m.entity_Type || m.entityType;
                              return entityType === 2 || entityType === 3 || 
                                     entityType === 'pac' || entityType === 'PAC' || entityType === 'Package' ||
                                     entityType === 'sep' || entityType === 'SER' || entityType === 'PackageSeries';
                            }).slice(0, 1).map((membership, index) => {
                              // Calculate package details
                              const totalAmount = membership.installments?.reduce((sum, inst) => sum + (inst.amount || 0), 0) || 0;
                              
                              // Get service and product names from membership details
                              const serviceNames = [];
                              const productNames = [];
                              if (membership.details && Array.isArray(membership.details)) {
                                membership.details.forEach(detail => {
                                  if (detail.serviceId) {
                                    const serviceName = getServiceNameById(detail.serviceId);
                                    if (serviceName && serviceName !== '-' && !serviceNames.includes(serviceName)) {
                                      serviceNames.push(serviceName);
                                    }
                                  }
                                  if (detail.productId) {
                                    const productName = getProductNameById(detail.productId);
                                    if (productName && productName !== '-' && !productNames.includes(productName)) {
                                      productNames.push(productName);
                                    }
                                  }
                                });
                              }
                              
                              return (
                                <div key={membership.membershipId || index} className="border rounded p-3">
                                  <div className="d-flex justify-content-between mb-2">
                                    <strong>{getEntityName(membership)}</strong>
                                    <span>{formatCurrency(totalAmount)} AED</span>
                                  </div>
                                  {serviceNames.length > 0 && (
                                    <div>
                                      <small><strong>Services:</strong> {serviceNames.join(', ')}</small>
                                    </div>
                                  )}
                                  {productNames.length > 0 && (
                                    <div>
                                      <small><strong>Products:</strong> {productNames.join(', ')}</small>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </CardBody>
                    </Card>

                  </Col>
                </Row>
              </TabPane>

              {/* Profile Tab */}
              <TabPane tabId="profile">
                <Row>
                  <Col md={6}>
                    <Card className="mb-3">
                      <CardBody>
                        <h6 className="mb-3 text-primary">Contact Information</h6>
                        <div className="mb-2">
                          <Mail size={16} className="me-2 text-muted" />
                          <strong>Email:</strong> {customerData.customer_Email || '-'}
                        </div>
                        <div className="mb-2">
                          <Phone size={16} className="me-2 text-muted" />
                          <strong>Contact:</strong> {customerData.customer_ContactNo || '-'}
                        </div>
                        {customerData.primaryContact_Name && (
                          <div className="mb-2">
                            <User size={16} className="me-2 text-muted" />
                            <strong>Primary Contact:</strong> {customerData.primaryContact_Name}
                            {customerData.primaryContact_Phone && ` (${customerData.primaryContact_Phone})`}
                          </div>
                        )}
                        <div className="mb-2">
                          <MapPin size={16} className="me-2 text-muted" />
                          <strong>Address (EN):</strong> {customerData.customer_AddressEn || '-'}
                        </div>
                        {customerData.customer_AddressAr && (
                          <div className="mb-2">
                            <MapPin size={16} className="me-2 text-muted" />
                            <strong>Address (AR):</strong> {customerData.customer_AddressAr}
                          </div>
                        )}
                        {customerData.customer_Country_Id && (() => {
                          const country = countries.find(c => c.country_Id === customerData.customer_Country_Id);
                          return country ? (
                            <div className="mb-2">
                              <MapPin size={16} className="me-2 text-muted" />
                              <strong>Country:</strong> {`${country.country_NameEn} - ${country.country_NameAr}`}
                            </div>
                          ) : null;
                        })()}
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="mb-3">
                      <CardBody>
                        <h6 className="mb-3 text-primary">Business Information</h6>
                        <div className="mb-2">
                          <CreditCard size={16} className="me-2 text-muted" />
                          <strong>Credit Limit:</strong> {formatCurrency(customerData.customer_CreditLimit)} AED
                        </div>
                        {advanceBalance !== null && (
                          <div className="mb-2">
                            <Receipt size={16} className="me-2 text-success" />
                            <strong>Advance Balance:</strong> {formatCurrency(advanceBalance?.availableAdvance || advanceBalance?.AvailableAdvance || 0)} AED
                          </div>
                        )}
                        {outstandingCredit !== null && (
                          <div className="mb-2">
                            <Receipt size={16} className="me-2 text-danger" />
                            <strong>Outstanding Credit:</strong> {formatCurrency(outstandingCredit?.totalOutstanding || outstandingCredit?.TotalOutstanding || 0)} AED
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </TabPane>

              {/* Notes Tab */}
              <TabPane tabId="notes">
                <Card>
                  <CardBody>
                    <h6 className="mb-3">Customer Notes</h6>
                    <p>{customerData.customer_Notes || 'No notes available'}</p>
                  </CardBody>
                </Card>
              </TabPane>

              {/* Appointment Tab */}
              <TabPane tabId="appointment">
                <Card>
                  <CardBody>
                    <h6 className="mb-3">All Appointments</h6>
                    {allBookings.length === 0 ? (
                      <Alert color="info">No appointments found</Alert>
                    ) : (
                      <Table responsive striped>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Date</th>
                            <th>Service</th>
                            <th>Time</th>
                            <th>Cost</th>
                            <th>Staff</th>
                          </tr>
                        </thead>
                        <tbody>
                          {expandBookingsToServices(allBookings).map((serviceRow, index) => (
                            <tr key={`${serviceRow.booking_Id}-${serviceRow.detailIndex}-${serviceRow.resourceIndex}-${index}`}>
                              <td>{index + 1}</td>
                              <td>{formatDate(serviceRow.date)}</td>
                              <td>{serviceRow.serviceName}</td>
                              <td>{serviceRow.time}</td>
                              <td>{formatCurrency(serviceRow.serviceCost || 0)} AED</td>
                              <td>{serviceRow.staff}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </CardBody>
                </Card>
              </TabPane>

              {/* Products Tab */}
              <TabPane tabId="products">
                <Card>
                  <CardBody>
                    <h6 className="mb-3">Products Purchased</h6>
                    {(() => {
                      const invoiceProducts = getProductsFromInvoices();
                      console.log('📦 Invoice products to display:', invoiceProducts);
                      console.log('📦 Master products array:', products.length, products);
                      
                      if (invoiceProducts.length === 0) {
                        return <Alert color="info">No products found in invoices</Alert>;
                      }
                      
                      return (
                        <Table responsive striped>
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Product Name</th>
                              <th>Total Quantity</th>
                              <th>Total Amount</th>
                              <th>Last Purchase</th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoiceProducts.map((item, index) => {
                              // Get productId from the item (stored as number in productMap)
                              const productId = item.productId;
                              
                              console.log('🔍 Looking up product ID:', productId, 'Type:', typeof productId);
                              console.log('🔍 Available master products:', products.length);
                              
                              // Look up product name from master products array (state variable) - convert to number for comparison
                              const productIdNum = productId ? parseInt(productId, 10) : null;
                              const productData = products.find(p => {
                                const pId = parseInt(p.product_Id || p.productId || p.id || p.value || 0, 10);
                                const match = pId === productIdNum && pId > 0;
                                if (match) {
                                  console.log('✅ Match found! Product:', p.product_NameEn, 'ID:', pId);
                                }
                                return match;
                              });
                              
                              console.log('✅ Found product data:', productData ? productData.product_NameEn : 'NOT FOUND');
                              
                              const productName = productData 
                                ? (productData.product_NameEn || productData.productName_En || productData.name || productData.label || `Product ${productId}`)
                                : (item.product?.product_NameEn || item.product?.productName_En || item.product?.name || item.product?.label || `Product ${productId || index}`);
                              
                              console.log('📦 Product name resolved:', productName);
                              
                              // Find last purchase date from invoices
                              let lastPurchaseDate = null;
                              for (const invoice of invoices) {
                                if (invoice.details && Array.isArray(invoice.details)) {
                                  const hasProduct = invoice.details.some(d => {
                                    const dProductId = parseInt(d.entityId || d.entity_Id || d.product_Id || d.productId || 0, 10);
                                    const entityType = d.entityType || d.entity_Type;
                                    return dProductId === productIdNum && dProductId > 0 && (entityType === 'pro' || entityType === 'product' || entityType === 'Product');
                                  });
                                  if (hasProduct) {
                                    lastPurchaseDate = invoice.Invoice_Date || invoice.invoice_Date;
                                    break; // Get the most recent (invoices are sorted)
                                  }
                                }
                              }
                              
                              return (
                                <tr key={productId || index}>
                                  <td>{index + 1}</td>
                                  <td>{productName}</td>
                                  <td>{item.totalQuantity}</td>
                                  <td>{formatCurrency(item.totalAmount)} AED</td>
                                  <td>{lastPurchaseDate ? formatDate(lastPurchaseDate) : '-'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      );
                    })()}
                  </CardBody>
                </Card>
              </TabPane>

              {/* Memberships Tab */}
              <TabPane tabId="memberships">
                <Card>
                  <CardBody>
                    <h6 className="mb-3">Customer Memberships</h6>
                    {memberships.length === 0 ? (
                      <Alert color="info">No memberships found</Alert>
                    ) : (
                      <Table responsive striped>
                        <thead>
                          <tr>
                            <th style={{ width: '30px' }}></th>
                            <th>#</th>
                            <th>Purchase Date</th>
                            <th>Entity Name</th>
                            <th>Branch</th>
                          </tr>
                        </thead>
                        <tbody>
                          {memberships.map((membership, index) => {
                            const membershipId = membership.membershipId || membership.MembershipId || index;
                            const isExpanded = expandedMemberships.has(membershipId);
                            const details = getMembershipDetails(membership);
                            const hasDetails = details.services.length > 0; // Only check services since products are commented out
                            
                            return (
                              <React.Fragment key={membershipId}>
                                <tr>
                                  <td style={{ verticalAlign: 'middle' }}>
                                    {hasDetails && (
                                      <Button
                                        color="link"
                                        size="sm"
                                        onClick={() => toggleMembershipExpansion(membershipId)}
                                        style={{ padding: 0, minWidth: 'auto' }}
                                      >
                                        {isExpanded ? (
                                          <ChevronDown size={16} style={{ transform: 'rotate(180deg)' }} />
                                        ) : (
                                          <ChevronDown size={16} />
                                        )}
                                      </Button>
                                    )}
                                  </td>
                                  <td>{index + 1}</td>
                                  <td>{formatDate(membership.purchaseDate || membership.PurchaseDate)}</td>
                                  <td>{getEntityName(membership)}</td>
                                  <td>{branches.find(b => b.branch_Id === (membership.branchId || membership.BranchId))?.branch_NameEn || '-'}</td>
                                </tr>
                                {isExpanded && hasDetails && (
                                  <tr>
                                    <td colSpan="5" style={{ padding: 0, borderTop: 'none' }}>
                                      <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderTop: '1px solid #dee2e6' }}>
                                        {/* Services Section */}
                                        {details.services.length > 0 && (
                                          <div className="mb-3">
                                            <h6 style={{ fontSize: '14px', fontWeight: 'bold', color: '#495057', marginBottom: '10px' }}>
                                              <i className="mdi mdi-spa me-2 text-primary"></i>
                                              Services
                                            </h6>
                                            <Table size="sm" bordered className="mb-0" style={{ backgroundColor: 'white' }}>
                                              <thead style={{ backgroundColor: '#e9ecef' }}>
                                                <tr>
                                                  <th style={{ fontSize: '12px', padding: '8px', color: '#212529' }}>Service Name</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>Package</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>Total Qty</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>Available Qty</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>Completed</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>In Progress</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>Pending</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {details.services.map((service, serviceIndex) => (
                                                  <tr key={`service-${service.serviceId}-${serviceIndex}`}>
                                                    <td style={{ fontSize: '12px', padding: '8px' }}>
                                                      <strong>{service.serviceName}</strong>
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      {service.packageId ? (
                                                        <span style={{ color: '#212529', fontWeight: 'normal' }}>{service.packageName}</span>
                                                      ) : (
                                                        <span style={{ color: '#212529', fontWeight: 'normal' }}>Direct</span>
                                                      )}
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      <span style={{ color: '#212529', fontWeight: '500' }}>{service.totalQty}</span>
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      <span style={{ color: '#212529', fontWeight: '500' }}>{service.availableQty}</span>
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      {service.completedQty > 0 ? (
                                                        <span style={{ color: '#212529', fontWeight: '500' }}>{service.completedQty}</span>
                                                      ) : (
                                                        <span style={{ color: '#212529' }}>-</span>
                                                      )}
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      {service.inProgressQty > 0 ? (
                                                        <span style={{ color: '#212529', fontWeight: '500' }}>{service.inProgressQty}</span>
                                                      ) : (
                                                        <span style={{ color: '#212529' }}>-</span>
                                                      )}
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      {service.pendingQty > 0 ? (
                                                        <span style={{ color: '#212529', fontWeight: '500' }}>{service.pendingQty}</span>
                                                      ) : (
                                                        <span style={{ color: '#212529' }}>-</span>
                                                      )}
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </Table>
                                          </div>
                                        )}
                                        
                                        {/* Products Section - Commented out as products are not selectable in packages/package series */}
                                        {/* Uncomment below section when products need to be displayed in membership tab */}
                                        {/* {details.products.length > 0 && (
                                          <div>
                                            <h6 style={{ fontSize: '14px', fontWeight: 'bold', color: '#495057', marginBottom: '10px' }}>
                                              <i className="mdi mdi-package-variant me-2 text-success"></i>
                                              Products
                                            </h6>
                                            <Table size="sm" bordered className="mb-0" style={{ backgroundColor: 'white' }}>
                                              <thead style={{ backgroundColor: '#e9ecef' }}>
                                                <tr>
                                                  <th style={{ fontSize: '12px', padding: '8px' }}>Product Name</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center' }}>Package</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center' }}>Total Qty</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center' }}>Available Qty</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center' }}>Completed</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center' }}>In Progress</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center' }}>Pending</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {details.products.map((product, productIndex) => (
                                                  <tr key={`product-${product.productId}-${productIndex}`}>
                                                    <td style={{ fontSize: '12px', padding: '8px' }}>
                                                      <strong>{product.productName}</strong>
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      {product.packageId ? (
                                                        <span style={{ color: '#6c757d' }}>{product.packageName}</span>
                                                      ) : (
                                                        <span style={{ color: '#6c757d' }}>Direct</span>
                                                      )}
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      <Badge color="secondary" style={{ color: '#fff' }}>{product.totalQty}</Badge>
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      <Badge color="info" style={{ color: '#fff' }}>{product.availableQty}</Badge>
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      {product.completedQty > 0 ? (
                                                        <Badge color="success" style={{ color: '#fff' }}>{product.completedQty}</Badge>
                                                      ) : (
                                                        <span style={{ color: '#6c757d' }}>-</span>
                                                      )}
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      {product.inProgressQty > 0 ? (
                                                        <Badge color="warning" style={{ color: '#212529' }}>{product.inProgressQty}</Badge>
                                                      ) : (
                                                        <span style={{ color: '#6c757d' }}>-</span>
                                                      )}
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      {product.pendingQty > 0 ? (
                                                        <Badge color="secondary" style={{ color: '#fff' }}>{product.pendingQty}</Badge>
                                                      ) : (
                                                        <span style={{ color: '#6c757d' }}>-</span>
                                                      )}
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </Table>
                                          </div>
                                        )} */}
                                        
                                        {!hasDetails && (
                                          <div className="text-center text-muted py-2">
                                            <small>No service details available</small>
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </Table>
                    )}
                  </CardBody>
                </Card>
              </TabPane>

              {/* Packages Tab */}
              <TabPane tabId="packages">
                <Card>
                  <CardBody>
                    <h6 className="mb-3">Customer Packages</h6>
                    {memberships.filter(m => {
                      const entityType = m.entity_Type || m.entityType;
                      return entityType === 2 || entityType === 3 || 
                             entityType === 'pac' || entityType === 'PAC' || entityType === 'Package' ||
                             entityType === 'sep' || entityType === 'SER' || entityType === 'PackageSeries';
                    }).length === 0 ? (
                      <Alert color="info">No packages found</Alert>
                    ) : (
                      <div className="row">
                        {memberships.filter(m => {
                          const entityType = m.entity_Type || m.entityType;
                          return entityType === 2 || entityType === 3 || 
                                 entityType === 'pac' || entityType === 'PAC' || entityType === 'Package' ||
                                 entityType === 'sep' || entityType === 'SER' || entityType === 'PackageSeries';
                        }).map((membership, index) => {
                          const totalAmount = membership.installments?.reduce((sum, inst) => sum + (inst.amount || 0), 0) || 0;
                          
                          // Get services with quantities from membership details
                          const serviceMap = new Map();
                          const productNames = [];
                          
                          if (membership.details && Array.isArray(membership.details)) {
                            membership.details.forEach(detail => {
                              if (detail.serviceId) {
                                const serviceId = detail.serviceId;
                                const serviceName = getServiceNameById(serviceId);
                                if (serviceName && serviceName !== '-') {
                                  if (!serviceMap.has(serviceId)) {
                                    serviceMap.set(serviceId, {
                                      name: serviceName,
                                      quantity: 0
                                    });
                                  }
                                  serviceMap.get(serviceId).quantity++;
                                }
                              }
                              if (detail.productId) {
                                const productName = getProductNameById(detail.productId);
                                if (productName && productName !== '-' && !productNames.includes(productName)) {
                                  productNames.push(productName);
                                }
                              }
                            });
                          }
                          
                          const servicesWithQty = Array.from(serviceMap.values());
                          
                          return (
                            <div key={membership.membershipId || index} className="col-md-6 mb-3">
                              <Card className="h-100">
                                <CardBody>
                                  <h6>{getEntityName(membership)}</h6>
                                  <p><strong>Price:</strong> {formatCurrency(totalAmount)} AED</p>
                                  {servicesWithQty.length > 0 && (
                                    <div className="mt-2">
                                      <strong>Services:</strong>
                                      <ul className="mb-0 mt-1" style={{ paddingLeft: '20px' }}>
                                        {servicesWithQty.map((service, serviceIndex) => (
                                          <li key={serviceIndex} style={{ color: '#212529' }}>
                                            {service.name} <strong>(Qty: {service.quantity})</strong>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {productNames.length > 0 && (
                                    <p className="mt-2"><strong>Products:</strong> {productNames.join(', ')}</p>
                                  )}
                                </CardBody>
                              </Card>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardBody>
                </Card>
              </TabPane>
            </TabContent>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CustomerProfileModal;